import "bootstrap/dist/css/bootstrap.min.css";
import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Toaster } from "react-hot-toast";
import { FaEdit, FaPlus, FaSearch, FaTrash } from "react-icons/fa";
import showToast from "../Component/ToastNotification";

function CategoryList() {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);

  // For modal state
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [categoryName, setCategoryName] = useState("");

  const fetchCategories = async () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/get-category`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }

        const result = await response.json();
        setCategories(result.categories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const filteredCategories = categories.filter((category) =>
    category.SuperCategoryName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCategories = filteredCategories.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
    useEffect(() => {
      setCurrentPage(1); // Reset to the first page when search term changes
    }, [searchTerm]);
  

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleModalClose = () => {
    resetForm();
    setShowModal(false);
  };

  const resetForm = () => {
    setCategoryName("");
    setCategoryId(null);
    setIsEditing(false);
  };

  const handleAddOrEditCategory = async () => {
    const token = localStorage.getItem("authToken");
    const payload = { CategoryName: categoryName };

    try {
      const url = isEditing
        ? `${process.env.REACT_APP_API_URL}/update-category`
        : `${process.env.REACT_APP_API_URL}/add-category`;
      const method = "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(
          isEditing ? { ...payload, ID: categoryId } : payload
        ),
      });

      if (!response.ok) {
        throw new Error(
          isEditing ? "Failed to update category" : "Failed to add category"
        );
      }
      showToast(
        isEditing ? "Category updated successfully!" : "Category added successfully!"
      );

      fetchCategories();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error("Error adding/updating category:", error);
      showToast("Something went wrong!", "error");
    }
  };

  const handleEditClick = (category) => {
    setCategoryId(category.SuperCategoryId);
    setCategoryName(category.SuperCategoryName);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDeleteClick = (categoryId) => {
    setCategoryId(categoryId);
    setShowDeleteModal(true);
  };

  const handleDeleteCategory = async () => {
    const token = localStorage.getItem("authToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/delete-category`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ID: categoryId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete category");
      }
      showToast("Category deleted successfully!");

      fetchCategories();
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting category:", error);
      showToast("Failed to delete category!", "error");
    }
  };

  return (
    
    <div className="container" style={{ marginTop: "80px" }}>
     <Toaster position="top-right" reverseOrder={false} />
      <h2 className="text-primary">Category List</h2>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="input-group me-3" style={{ maxWidth: "300px" }}>
          <span className="input-group-text bg-primary text-white">
            <FaSearch />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn btn-success" onClick={() => setShowModal(true)}>
          <FaPlus /> Add Category
        </button>
      </div>

      <table className="table table-bordered table-striped">
        <thead className="table-primary">
          <tr>
            <th>ID</th>
            <th>Main Category</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentCategories.map((category, index) => (
            <tr key={category.SuperCategoryId}>
              <td>{index + 1 + (currentPage - 1) * itemsPerPage}</td>
              <td>{category.SuperCategoryName}</td>
              <td>
                <button
                  className="btn btn-sm btn-outline-success me-2"
                  onClick={() => handleEditClick(category)}
                >
                  <FaEdit /> Edit
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDeleteClick(category.SuperCategoryId)}
                >
                  <FaTrash /> Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <nav>
        <ul className="pagination justify-content-center">
          {[...Array(totalPages)].map((_, index) => (
            <li
              key={index}
              className={`page-item ${
                currentPage === index + 1 ? "active" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <Modal show={showModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Edit Category" : "Add New Category"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="categoryName">
              <Form.Label>Category Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter category name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
              />
            </Form.Group>

          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleAddOrEditCategory}>
            {isEditing ? "Update Category" : "Add Category"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this category?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteCategory}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}


export default CategoryList;